import { motion } from "framer-motion";

/**
 * Horizontal flow diagram. Each step is a rounded box colour-coded by type:
 *   - "local"     → muted grey  (#9B9589)
 *   - "cacheable" → amber       (#E8A930)
 *   - "api"       → terracotta  (#C4521E)
 *
 * Steps are scrollable horizontally on narrow screens.
 */
const COLOR = {
  local: { border: "#DDD9CF", bg: "#FFFFFF", label: "#56534A", pill: "#9B9589" },
  cacheable: { border: "#E8A930", bg: "#FBF1DC", label: "#7A5A12", pill: "#E8A930" },
  api: { border: "#C4521E", bg: "#FBE4D8", label: "#7A2E0C", pill: "#C4521E" },
};

const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#56534A] mt-4" data-testid="flow-legend">
    {[
      { type: "local", label: "Local logic — free" },
      { type: "cacheable", label: "Cacheable — minimal cost" },
      { type: "api", label: "API required — costs money" },
    ].map(({ type, label }) => (
      <div key={type} className="flex items-center gap-2">
        <span
          className="inline-block w-3 h-3 rounded-sm border"
          style={{ borderColor: COLOR[type].pill, backgroundColor: type === "local" ? "transparent" : COLOR[type].bg }}
        />
        <span>{label}</span>
      </div>
    ))}
  </div>
);

export const FlowDiagram = ({ steps }) => {
  if (!steps?.length) return null;
  return (
    <section className="mt-8" data-testid="feature-flow">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#56534A] mb-4">
        Feature flow — where AI is actually needed
      </div>

      <div className="overflow-x-auto -mx-6 md:mx-0">
        <div className="flex items-stretch gap-2 px-6 md:px-0 min-w-max">
          {steps.map((s, i) => {
            const type = COLOR[s.type] ? s.type : "local";
            const c = COLOR[type];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center"
                data-testid={`flow-step-${i}`}
              >
                <div
                  className="rounded-md border p-3 w-[140px] xl:w-[160px] flex flex-col justify-between min-h-[110px]"
                  style={{ borderColor: c.border, backgroundColor: c.bg, color: c.label }}
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] opacity-70">
                      Step {s.step ?? i + 1}
                    </div>
                    <div className="font-medium text-sm leading-snug mt-1 text-[#1A1814]">
                      {s.label}
                    </div>
                    {s.sublabel && (
                      <div className="text-[11px] mt-1 opacity-80">{s.sublabel}</div>
                    )}
                  </div>
                  {s.cost_per_call && (
                    <div
                      className="text-[10px] mt-2 inline-block self-start px-1.5 py-0.5 rounded-sm"
                      style={{
                        color: type === "local" ? "#56534A" : c.label,
                        backgroundColor: type === "local" ? "transparent" : "rgba(255,255,255,0.5)",
                      }}
                    >
                      {s.cost_per_call}
                    </div>
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div className="w-6 flex items-center justify-center text-[#9B9589]">→</div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      <Legend />
    </section>
  );
};
